import React, { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, Heart, Scale, Compass, Sparkles, 
  Target, Users, Zap, BookOpen, ArrowRight,
  Award, TrendingUp, Shield, Clock, Leaf,
  Activity, Eye, Calendar, FileText, Lightbulb
} from 'lucide-react';

// Import all mental health tools
import { EmotionWheel } from '@/components/mental/EmotionWheel';
import { BodySensationMapper } from '@/components/mental/BodySensationMapper';
import { GuidedReframingExercise } from '@/components/mental/GuidedReframingExercise';
import { ValuesExplorer } from '@/components/mental/ValuesExplorer';
import { SelfCompassionExercise } from '@/components/mental/SelfCompassionExercise';
import { BehaviouralActivation } from '@/components/mental/BehaviouralActivation';
import { SchemaExplorer } from '@/components/mental/SchemaExplorer';

// New exercises with tracking
import { WorryPostponement } from '@/components/mental/exercises/WorryPostponement';
import { DefusionTechniques } from '@/components/mental/exercises/DefusionTechniques';
import { ProblemSolvingWorkshop } from '@/components/mental/exercises/ProblemSolvingWorkshop';
import { GratitudeJournal } from '@/components/mental/exercises/GratitudeJournal';
import { BehaviouralExperiment } from '@/components/mental/exercises/BehaviouralExperiment';
import { ExposureTherapy } from '@/components/mental/exercises/ExposureTherapy';
import { ValuesInAction } from '@/components/mental/exercises/ValuesInAction';
import { MindfulnessGrounding } from '@/components/mental/exercises/MindfulnessGrounding';
import { ActivityScheduling } from '@/components/mental/exercises/ActivityScheduling';
import { SelfCompassionBreak } from '@/components/mental/exercises/SelfCompassionBreak';
import { ProgressiveMuscleRelaxation } from '@/components/mental/exercises/ProgressiveMuscleRelaxation';
import { ExerciseProgressDashboard } from '@/components/mental/ExerciseProgressDashboard';

// Tool categories
const TOOL_CATEGORIES = [
  { id: 'awareness', label: 'Self-Awareness', icon: Brain, tools: ['emotion-wheel', 'body-mapping', 'schema-explorer'] },
  { id: 'cbt', label: 'CBT Tools', icon: Scale, tools: ['reframing', 'behavioural-experiment', 'problem-solving'] },
  { id: 'act', label: 'ACT & Values', icon: Compass, tools: ['values', 'values-action', 'defusion', 'activation'] },
  { id: 'anxiety', label: 'Anxiety', icon: Zap, tools: ['worry-postponement', 'exposure', 'grounding', 'pmr'] },
  { id: 'mood', label: 'Mood & Wellbeing', icon: Heart, tools: ['gratitude', 'activity-scheduling', 'self-compassion', 'self-compassion-break'] }
];

const TOOLS: Record<string, any> = {
  'emotion-wheel': { id: 'emotion-wheel', name: 'Emotion Wheel', description: 'Explore emotions with precision', icon: Heart, colour: 'from-pink-500/20 to-rose-500/20', borderColour: 'border-pink-500/30', xp: 5, category: 'awareness' },
  'body-mapping': { id: 'body-mapping', name: 'Body Sensation Mapper', description: 'Track where emotions live in your body', icon: Target, colour: 'from-orange-500/20 to-amber-500/20', borderColour: 'border-orange-500/30', xp: 5, category: 'awareness' },
  'reframing': { id: 'reframing', name: 'Guided Reframing', description: 'Challenge unhelpful thoughts with CBT', icon: Scale, colour: 'from-blue-500/20 to-cyan-500/20', borderColour: 'border-blue-500/30', xp: 10, category: 'cbt' },
  'values': { id: 'values', name: 'Values Explorer', description: 'Clarify what matters most', icon: Compass, colour: 'from-emerald-500/20 to-green-500/20', borderColour: 'border-emerald-500/30', xp: 10, category: 'act' },
  'self-compassion': { id: 'self-compassion', name: 'Self-Compassion', description: 'Practice kindness toward yourself', icon: Sparkles, colour: 'from-purple-500/20 to-pink-500/20', borderColour: 'border-purple-500/30', xp: 15, category: 'mood' },
  'activation': { id: 'activation', name: 'Behavioural Activation', description: 'Break the cycle of low mood', icon: Zap, colour: 'from-yellow-500/20 to-orange-500/20', borderColour: 'border-yellow-500/30', xp: 5, category: 'act' },
  'schema-explorer': { id: 'schema-explorer', name: 'Schema Explorer', description: 'Discover deep patterns from your past', icon: Shield, colour: 'from-indigo-500/20 to-purple-500/20', borderColour: 'border-indigo-500/30', xp: 15, category: 'awareness' },
  // New tracked exercises
  'worry-postponement': { id: 'worry-postponement', name: 'Worry Postponement', description: 'Schedule worry time to reduce rumination', icon: Clock, colour: 'from-amber-500/20 to-yellow-500/20', borderColour: 'border-amber-500/30', xp: 15, category: 'anxiety' },
  'defusion': { id: 'defusion', name: 'Cognitive Defusion', description: 'Unhook from difficult thoughts (ACT)', icon: Lightbulb, colour: 'from-cyan-500/20 to-teal-500/20', borderColour: 'border-cyan-500/30', xp: 15, category: 'act' },
  'problem-solving': { id: 'problem-solving', name: 'Problem Solving', description: 'Structured approach to challenges', icon: FileText, colour: 'from-slate-500/20 to-gray-500/20', borderColour: 'border-slate-500/30', xp: 20, category: 'cbt' },
  'gratitude': { id: 'gratitude', name: 'Gratitude Journal', description: 'Build positivity through appreciation', icon: Leaf, colour: 'from-green-500/20 to-emerald-500/20', borderColour: 'border-green-500/30', xp: 10, category: 'mood' },
  'behavioural-experiment': { id: 'behavioural-experiment', name: 'Behavioural Experiment', description: 'Test beliefs through action', icon: Target, colour: 'from-violet-500/20 to-purple-500/20', borderColour: 'border-violet-500/30', xp: 25, category: 'cbt' },
  'exposure': { id: 'exposure', name: 'Exposure Therapy', description: 'Face fears with a graded hierarchy', icon: TrendingUp, colour: 'from-amber-500/20 to-orange-500/20', borderColour: 'border-amber-500/30', xp: 25, category: 'anxiety' },
  'values-action': { id: 'values-action', name: 'Values in Action', description: 'Commit to meaningful action', icon: Compass, colour: 'from-emerald-500/20 to-teal-500/20', borderColour: 'border-emerald-500/30', xp: 20, category: 'act' },
  'grounding': { id: 'grounding', name: 'Mindfulness & Grounding', description: '5-4-3-2-1 and body scan', icon: Eye, colour: 'from-teal-500/20 to-cyan-500/20', borderColour: 'border-teal-500/30', xp: 15, category: 'anxiety' },
  'activity-scheduling': { id: 'activity-scheduling', name: 'Activity Scheduling', description: 'Plan balanced meaningful activities', icon: Calendar, colour: 'from-indigo-500/20 to-purple-500/20', borderColour: 'border-indigo-500/30', xp: 15, category: 'mood' },
  'self-compassion-break': { id: 'self-compassion-break', name: 'Self-Compassion Break', description: 'Treat yourself with kindness', icon: Heart, colour: 'from-rose-500/20 to-pink-500/20', borderColour: 'border-rose-500/30', xp: 20, category: 'mood' },
  'pmr': { id: 'pmr', name: 'Progressive Muscle Relaxation', description: 'Release tension systematically', icon: Activity, colour: 'from-violet-500/20 to-purple-500/20', borderColour: 'border-violet-500/30', xp: 20, category: 'anxiety' }
};

export default function MentalToolsHub() {
  useNavigationHistory();
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredTools = activeCategory === 'all' 
    ? Object.values(TOOLS)
    : Object.values(TOOLS).filter(t => t.category === activeCategory);

  const renderTool = () => {
    switch (activeTool) {
      case 'emotion-wheel':
        return (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setActiveTool(null)}>
              ← Back to Tools
            </Button>
            <EmotionWheel 
              onEmotionSelect={(emotion, category) => {
                // Handle emotion selection
              }}
            />
          </div>
        );
      case 'body-mapping':
        return (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setActiveTool(null)}>
              ← Back to Tools
            </Button>
            <BodySensationMapper 
              onSensationsChange={(sensations) => {
                // Handle sensations change
              }}
            />
          </div>
        );
      case 'reframing':
        return (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setActiveTool(null)}>
              ← Back to Tools
            </Button>
            <GuidedReframingExercise 
              onComplete={(data) => {
                setActiveTool(null);
              }}
              onCancel={() => setActiveTool(null)}
            />
          </div>
        );
      case 'values':
        return (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setActiveTool(null)}>
              ← Back to Tools
            </Button>
            <ValuesExplorer 
              onValuesChange={(values) => {
                // Handle values change
              }}
            />
          </div>
        );
      case 'self-compassion':
        return (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setActiveTool(null)}>
              ← Back to Tools
            </Button>
            <SelfCompassionExercise 
              onComplete={(data) => {
                // Exercise completed
              }}
            />
          </div>
        );
      case 'activation':
        return (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setActiveTool(null)}>
              ← Back to Tools
            </Button>
            <BehaviouralActivation 
              onActivityComplete={(activity) => {
                // Activity completed
              }}
            />
          </div>
        );
      case 'schema-explorer':
        return (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setActiveTool(null)}>
              ← Back to Tools
            </Button>
            <SchemaExplorer 
              onSchemaSelect={(schemas) => {
                // Schemas selected
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (activeTool) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title={TOOLS[activeTool as keyof typeof TOOLS]?.name || 'Tool'} />
        <div className="container mx-auto p-4 max-w-4xl">
          {renderTool()}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Mental Wellness Tools" />

      <div className="container mx-auto p-4 max-w-5xl">
        {/* Hero Section */}
        <Card className="mb-6 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-blue-500/10 border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start gap-4">
              <div className="p-4 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                <Brain className="w-10 h-10 text-purple-500" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">Therapeutic Tools</h1>
                <p className="text-muted-foreground mb-4">
                  Evidence-based psychological tools at your fingertips. These aren't just exercises - 
                  they're the same techniques used by therapists worldwide. Each tool builds 
                  self-awareness and emotional regulation skills.
                </p>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Award className="w-4 h-4 text-amber-500" />
                    <span>Earn XP</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span>Track progress</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <span>AI integration</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={activeCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveCategory('all')}
          >
            All Tools
          </Button>
          {TOOL_CATEGORIES.map(cat => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveCategory(cat.id)}
              className="gap-1"
            >
              <cat.icon className="w-4 h-4" />
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Tools Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {filteredTools.map(tool => (
            <Card 
              key={tool.id}
              className={`cursor-pointer hover:scale-[1.02] transition-all bg-gradient-to-br ${tool.colour} ${tool.borderColour} hover:shadow-lg`}
              onClick={() => setActiveTool(tool.id)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-background/50">
                      <tool.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{tool.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          +{tool.xp} XP
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>{tool.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Card 
            className="cursor-pointer hover:border-primary/50 transition-all"
            onClick={() => navigate('/thought-tracker')}
          >
            <CardContent className="pt-4 flex items-center gap-3">
              <Brain className="w-8 h-8 text-primary" />
              <div>
                <h4 className="font-semibold">Thought Tracker</h4>
                <p className="text-xs text-muted-foreground">Log and analyse thoughts</p>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:border-accent/50 transition-all"
            onClick={() => navigate('/therapy', { state: { mode: 'cbt' } })}
          >
            <CardContent className="pt-4 flex items-center gap-3">
              <Users className="w-8 h-8 text-accent" />
              <div>
                <h4 className="font-semibold">Talk to Therappi</h4>
                <p className="text-xs text-muted-foreground">AI reflection support</p>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:border-purple-500/50 transition-all"
            onClick={() => navigate('/my-scores')}
          >
            <CardContent className="pt-4 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-500" />
              <div>
                <h4 className="font-semibold">My Progress</h4>
                <p className="text-xs text-muted-foreground">View wellness scores</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Psychoeducation Footer */}
        <Card className="mt-6 border-dashed">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-muted-foreground shrink-0" />
              <div>
                <h4 className="font-semibold text-sm">About These Tools</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  These tools draw from Cognitive Behavioural Therapy (CBT), Acceptance and Commitment 
                  Therapy (ACT), Compassion-Focused Therapy (CFT), and Schema Therapy. They're designed 
                  to complement, not replace, professional support. If you're struggling, please reach 
                  out to a mental health professional.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
